import { Component, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export type NoticeSeverity = 'warning' | 'error';

/**
 * A yellow warning or red error banner for forms, dialogs and pages. Same look as `<lib-page-notice>`,
 * but closing it isn't remembered: it comes back whenever the parent renders it again.
 */
@Component({
  selector: 'lib-notice',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    @if (!closed()) {
      <div
        class="notice"
        [class.notice-error]="severity() === 'error'"
        [attr.role]="severity() === 'error' ? 'alert' : 'status'"
      >
        <mat-icon aria-hidden="true">{{ icon() ?? (severity() === 'error' ? 'error' : 'warning') }}</mat-icon>
        <p><ng-content /></p>
        @if (dismissible()) {
          <button
            mat-icon-button
            class="notice-close"
            matTooltip="Dismiss"
            aria-label="Dismiss"
            (click)="dismiss()"
          >
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .notice {
      --notice-accent: #8a5300;
      display: flex;
      align-items: center;
      gap: 12px;
      min-height: 40px;
      padding: 4px 4px 4px 16px;
      border-left: 3px solid var(--notice-accent);
      background: #fff4e5;
      font-size: 13px;
      line-height: 1.5;

      > mat-icon {
        flex: none;
        color: var(--notice-accent);
      }

      p {
        flex: 1;
        margin: 0;
        padding: 6px 0;
      }
    }

    .notice-error {
      --notice-accent: #b3261e;
      background: #fce8e6;
    }

    .notice-close {
      flex: none;
    }
  `,
})
export class Notice {
  readonly severity = input<NoticeSeverity>('warning');
  /** Material icon name; defaults to the severity's icon (e.g. a red date mismatch uses `warning`, the triangle). */
  readonly icon = input<string | null>(null);
  readonly dismissible = input(false);
  readonly dismissed = output<void>();

  protected readonly closed = signal(false);

  protected dismiss(): void {
    this.closed.set(true);
    this.dismissed.emit();
  }
}
